// Login.tsx
import { LogIn, RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { env } from '@/shared/config';
import { useAuthWithUserData } from '@/store/auth';
import Button from '@/shared/ui/Button';

export default function Login() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const { setAuth } = useAuthWithUserData();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';

  const popupRef = useRef<Window | null>(null);
  const checkPopupRef = useRef<number | ReturnType<typeof setTimeout> | null>(
    null
  );

  const messageReceivedRef = useRef(false);
  const loadingRef = useRef(false);

  const handleMessage = (event: MessageEvent) => {
    const normalizeOrigin = (origin: string) => {
      return origin.replace(/\/$/, '').toLowerCase();
    };

    const eventOrigin = normalizeOrigin(event.origin);
    const allowedOrigins = [
      window.location.origin,
      env.apiBase,
      env.frontendUrl,
    ]
      .filter(Boolean)
      .map(normalizeOrigin);

    if (!allowedOrigins.includes(eventOrigin)) {
      return;
    }

    if (event.data.type === 'B24_AUTH_SUCCESS') {
      const { userId, role } = event.data;

      setAuth(Number(userId), role);
      setLoading(false);
      setError(false);
      messageReceivedRef.current = true;
      loadingRef.current = false;

      if (checkPopupRef.current) {
        clearInterval(checkPopupRef.current);
        checkPopupRef.current = null;
      }

      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
        popupRef.current = null;
      }

      toast.success('Успешный вход через Bitrix24');

      // Убираем обработчик при успешной авторизации
      window.removeEventListener('message', handleMessage);

      // Редирект только после успешной авторизации
      navigate(from, { replace: true });
    }

    // Обработка ошибок от popup
    if (event.data.type === 'B24_AUTH_ERROR') {
      setLoading(false);
      setError(true);
      messageReceivedRef.current = true;
      loadingRef.current = false;

      toast.error(event.data.message || 'Ошибка авторизации');

      if (checkPopupRef.current) {
        clearInterval(checkPopupRef.current);
        checkPopupRef.current = null;
      }

      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
        popupRef.current = null;
      }

      // Убираем обработчик при ошибке
      window.removeEventListener('message', handleMessage);
    }
  };

  const startAuth = () => {
    // Добавляем обработчик сообщений только при запуске авторизации
    window.addEventListener('message', handleMessage);

    setLoading(true);
    setError(false);
    loadingRef.current = true;
    messageReceivedRef.current = false;

    const width = 600;
    const height = 700;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    const authUrl = `${env.apiBase}/${env.apiPrefix}/auth/login`;

    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }

    const popup = window.open(
      authUrl,
      'bitrix24-auth',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );

    popupRef.current = popup;

    if (!popup) {
      toast.error('Пожалуйста, разрешите всплывающие окна для этого сайта');
      setLoading(false);
      setError(true);
      loadingRef.current = false;
      // Убираем обработчик при ошибке открытия popup
      window.removeEventListener('message', handleMessage);
      return;
    }

    checkPopupRef.current = setInterval(() => {
      const isClosed = !popup || popup.closed;

      if (isClosed) {
        clearInterval(checkPopupRef.current!);
        checkPopupRef.current = null;

        setTimeout(() => {
          if (!messageReceivedRef.current && loadingRef.current) {
            setLoading(false);
            setError(true);
            loadingRef.current = false;
            toast.error('Аутентификация не удалась. Попробуйте снова.');
            // Убираем обработчик при закрытии popup без авторизации
            window.removeEventListener('message', handleMessage);
          }
        }, 1000);
      }
    }, 500);
  };

  // Очистка при размонтировании компонента
  useEffect(() => {
    return () => {
      if (checkPopupRef.current) {
        clearInterval(checkPopupRef.current);
      }
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Показываем основной экран с кнопкой входа
  return (
    <div className="min-h-screen bg-gray-50 grid place-items-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <LogIn className="h-8 w-8 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Вход в систему
            </h1>
            <p className="text-gray-600">
              Для доступа к системе требуется авторизация через Bitrix24
            </p>
          </div>

          {!loading && !error && (
            <Button
              onClick={startAuth}
              className="w-full bg-blue-600 hover:bg-blue-700 py-3 text-lg"
            >
              <>
                <LogIn className="h-5 w-5 mr-2" />
                Войти через Bitrix24
              </>
            </Button>
          )}

          {loading && (
            <div className="text-center py-4">
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <span className="text-lg font-medium">
                  Авторизация через Bitrix24...
                </span>
              </div>
              <p className="text-sm text-gray-600">
                Пожалуйста, завершите авторизацию в открывшемся окне
              </p>
            </div>
          )}

          {error && (
            <div className="space-y-4">
              <Button
                onClick={startAuth}
                className="w-full bg-blue-600 hover:bg-blue-700 py-3"
              >
                <RefreshCw className="h-5 w-5 mr-2" />
                Попробовать снова
              </Button>

              <div className="text-xs text-gray-500">
                <p className="mb-2">Если проблема повторяется:</p>
                <ul className="space-y-1 text-left">
                  <li>• Проверьте блокировку всплывающих окон</li>
                  <li>• Убедитесь, что у вас есть доступ к Bitrix24</li>
                  <li>• Попробуйте обновить страницу</li>
                </ul>
              </div>
            </div>
          )}

          {!loading && !error && (
            <p className="text-xs text-gray-500 mt-4">
              После нажатия кнопки откроется окно авторизации Bitrix24
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
